import {
  BackEndValueType,
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  ISymbol,
  IWorkFlow,
  IWorkFlowDto
} from "@kuika/kuika-cl-model";
import { IProjectsWithWFResponseDto } from "@kuika/kuika-cl-model/src/dtos/projects-response";
import _ from "lodash";
import { WorkFlowService } from "../../../../services/workflow-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import { SymbolPicker } from "./symbol-picker";

export interface IWFStartSymbolsGroupState {
  isExternalProcess: boolean;
}

export interface IWFStartSymbolsGroupProps {
  parameters: IActionParameter[];
  action?: IAction;
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  updateParams: (parameters: IActionParameter[]) => void;
}

class WFStartSymbolsGroup extends PureSdComponent<
  IWFStartSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IWFStartSymbolsGroupState
> {
  constructor(props: IWFStartSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isExternalProcess: false
    };
  }

  componentDidMount(): void {
    this._fixProjectId();
    const next = this._checkIsExternalProcess();
    if (next !== this.state.isExternalProcess) {
      this.setState({ isExternalProcess: next });
    }
  }

  componentDidUpdate(prevProps: Readonly<IWFStartSymbolsGroupProps & IWithSDProps & IWithContextProp>): void {
    const prevPid = prevProps.parameters.find((p) => p.name === "projectId")?.symbol?.fixedValue;
    const currPid = this.props.parameters.find((p) => p.name === "projectId")?.symbol?.fixedValue;
    const sdChanged = prevProps.sd.project.id !== this.props.sd.project.id;

    if (prevPid !== currPid || sdChanged) {
      const next = this._checkIsExternalProcess();
      if (next !== this.state.isExternalProcess) {
        this.setState({ isExternalProcess: next });
      }
    }
  }

  _fixProjectId = () => {
    const projectId = this.getParameterWithName("projectId")?.symbol?.fixedValue;
    const params = _.cloneDeep(this.props.parameters);
    if (!projectId) {
      params.push({
        name: "projectId",
        symbol: {
          category: ESymbolCategory.fixed,
          label: "Project",
          description: "Project",
          fixedValue: this.props.sd.project.id
        },
        cardinality: ECardinality.One,
        Typename: "String" as BackEndValueType,
        isOptionalParameter: false
      });
    } else {
      const paramValueToUpdate = params.find((x) => x.name === "projectId");
      if (paramValueToUpdate && paramValueToUpdate.symbol.fixedValue.length == 0) {
        paramValueToUpdate.symbol.fixedValue = this.props.sd.project.id;
      }
    }
    this.props.updateParams(params);
  };

  _checkIsExternalProcess = () => {
    const projectId = this.getParameterWithName("projectId")?.symbol?.fixedValue;
    if (projectId === "") return true;
    if (projectId === undefined || projectId === null) return false;
    return projectId !== this.props.sd.project.id;
  };

  getParameterWithName = (name: "workflow" | "parameters" | "projectId"): IActionParameter | undefined => {
    return this.props.parameters.find((x) => x.name === name);
  };

  getworkflowSelectValue = () => {
    const workflow = this.getParameterWithName("workflow");
    if (workflow?.symbol?.fixedValue) {
      return workflow.symbol.fixedValue;
    }
    return "";
  };

  getProjectSelectValue = () => {
    const project = this.getParameterWithName("projectId");
    if (project?.symbol?.fixedValue) {
      return project.symbol.fixedValue;
    }
    return "";
  };

  setWorkFlowDetails = async (workFlowId: string) => {
    const { workFlowList } = this.props.sd;
    if (workFlowList === undefined) return undefined;
    const workFlow = workFlowList.find((wf) => wf.id === workFlowId);
    if (workFlow?.parameters || workFlowId === undefined) {
      return;
    }
    try {
      if (this.props.sd.project?.id !== undefined) {
        const res = await WorkFlowService.Get(workFlowId);
        if (res && res.status === 200) {
          this.props.sd.updateWorkFlowList(res.data);
        }
      }
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  getWorkFlowParameters = () => {
    const params = _.cloneDeep(this.props.parameters);
    const workflowParameter = params.find((x) => x.name === "workflow");
    const workFlowId = workflowParameter?.symbol?.fixedValue;
    const workflow = this.props.sd.workFlowList.find((wf) => wf.id === workFlowId);
    const actionParameters = workflow?.parameters; // .parameters?.filter((wf) => wf.parameterType === EWFParamType.Input);
    const valuesToUpdate = {
      actionParameters
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate, ESdChangeReason.WFStepMappingChange);
    return actionParameters;
  };

  renderworkflow = (parameter: IActionParameter): JSX.Element => {
    const projectId = this.getParameterWithName("projectId")?.symbol?.fixedValue;
    const isExternalProcess = this._checkIsExternalProcess();

    const project = isExternalProcess ? this.props.sd.projectsWithWFData.find((p) => p.id == projectId) : undefined;
    const externalWorkflows: (IWorkFlowDto | IWorkFlow)[] = project?.workflows ?? [];
    const workflowOptions: KuikaSelectboxOption[] = isExternalProcess
      ? externalWorkflows.map((wf: IWorkFlowDto | IWorkFlow): KuikaSelectboxOption => ({
          key: wf.id,
          value: wf.id,
          label: wf.name
        }))
      : (this.props.sd.workFlowList ?? []).map((wf: IWorkFlow): KuikaSelectboxOption => ({
          key: wf.id,
          value: wf.id,
          label: wf.name
        }));

    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Process:
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            size="sm"
            style={{ width: "100%" }}
            value={this.getworkflowSelectValue()}
            onSelect={(value: string | number | null): void => {
              if (typeof value === "string") this.onSelectworkflow(value);
            }}
            className="kuika_add_actions_inputs_size"
            options={workflowOptions}
          />
        </div>
      </>
    );
  };

  renderProjectSelection = (parameter: IActionParameter): JSX.Element => {
    if (!this._checkIsExternalProcess()) {
      return <></>;
    }
    const projectOptions: KuikaSelectboxOption[] = (this.props.sd.projectsWithWFData ?? [])
      .filter((p: IProjectsWithWFResponseDto): boolean => p.id !== this.props.sd.project.id)
      .map((p: IProjectsWithWFResponseDto): KuikaSelectboxOption => ({ key: p.id, value: p.id, label: p.name }));

    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          External Project:
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            size="sm"
            style={{ width: "100%" }}
            value={this.getProjectSelectValue()}
            onChange={this.onSelectProject}
            className="kuika_add_actions_inputs_size"
            options={projectOptions}
          />
        </div>
      </>
    );
  };

  onSelectworkflow = (val: string): void => {
    const params = _.cloneDeep(this.props.parameters);
    const workflowParameter = params.find((x) => x.name === "workflow");

    params.map((p) => (p.isOptionalParameter = false));
    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;

    if (workflowParameter && workflowParameter?.symbol === undefined) {
      workflowParameter.symbol = { category: ESymbolCategory.fixed } as ISymbol;
    }

    if (workflowParameter?.symbol) {
      workflowParameter.symbol.fixedValue = val;
      this.setWorkFlowDetails(val);
      this.props.updateValues(valuesToUpdate);
    }
  };

  onSelectProject = (val: string): void => {
    const params = _.cloneDeep(this.props.parameters);
    const projectParameter = params.find((x) => x.name === "projectId");
    const workflowParameter = params.find((x) => x.name === "workflow");

    params.map((p) => (p.isOptionalParameter = false));
    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;

    if (projectParameter && projectParameter?.symbol === undefined) {
      projectParameter.symbol = { category: ESymbolCategory.fixed } as ISymbol;
    }

    if (projectParameter?.symbol) {
      projectParameter.symbol.fixedValue = val;
      // this.setWorkFlowDetails(val);

      if (workflowParameter) {
        workflowParameter.symbol.fixedValue = "";
      }

      this.props.updateValues(valuesToUpdate);
    }
  };

  onChangeStartExternalProcess = (val: boolean) => {
    const params = _.cloneDeep(this.props.parameters);
    const projectParameter = params.find((x) => x.name === "projectId");
    if (projectParameter) {
      projectParameter.symbol.fixedValue = val ? "" : this.props.sd.project.id;
    } else {
      params.push({
        name: "projectId",
        symbol: {
          category: ESymbolCategory.fixed,
          label: "Project",
          description: "Project",
          fixedValue: val ? "" : this.props.sd.project.id
        },
        cardinality: ECardinality.One,
        Typename: "String" as BackEndValueType,
        isOptionalParameter: false
      });
    }
    const workflowParameter = params.find((x) => x.name === "workflow");
    if (workflowParameter) {
      workflowParameter.symbol.fixedValue = "";
    }

    this.props.updateParams(params);
    this.setState({ isExternalProcess: val });
  };

  render() {
    if (this.props.parameters.length <= 0) return <></>;
    // const activeStartParameters = this.getWorkFlowParameters();
    const isExternalProcess = this._checkIsExternalProcess();

    return (
      <div style={{ width: "100%" }}>
        <span>
          <KuikaSwitch
            label="Start External Process"
            onChange={this.onChangeStartExternalProcess}
            checked={this.state.isExternalProcess}
          />
        </span>
        {this.props.parameters.map((parameter, id) => {
          return <div key={id}>{parameter.name === "projectId" && this.renderProjectSelection(parameter)}</div>;
        })}
        {this.props.parameters.map((parameter, id) => {
          return <div key={id}>{parameter.name === "workflow" && this.renderworkflow(parameter)}</div>;
        })}

        {this.props.parameters
          ?.filter((p) => p.name !== "workflow" && p.name !== "parameters" && p.name !== "projectId")
          .map((parameter) => {
            if ((parameter.isOptionalParameter && parameter.isExternalVisible) || !parameter.isOptionalParameter)
              return (
                <div className={parameter.isDeletedOnSource === true ? "isDeletedOnSource" : ""} key={parameter.name}>
                  <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                    {parameter.name} ({parameter.Typename.toString()})
                  </span>

                  <div
                    className="kuika_styling_typography_wrapper kuika_screen-input__wrapper"
                    style={{ width: "100%" }}
                  >
                    <SymbolPicker
                      changeType="onBlur"
                      symbol={this.props.parameters.find((p) => p.name === parameter.name)?.symbol}
                      valueType={parameter.Typename}
                      targetCardinality={ECardinality.One}
                      hideWFParams={false}
                      action={this.props.action}
                      onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                        this.changeActionParameter(parameter.name, symbol, parameter.Typename, changeReason)
                      }
                      bindingParameter={parameter}
                    />
                    {parameter.isDeletedOnSource === true && (
                      <div
                        className="kuika_styling_typography_footer"
                        style={{ padding: "4px 6px", display: "flow-root" }}
                      >
                        {
                          <div style={{ float: "right" }} onClick={() => this.deleteParameter(parameter.name)}>
                            Delete
                          </div>
                        }
                      </div>
                    )}
                  </div>
                </div>
              );
          })}
      </div>
    );
  }

  deleteParameter = (parameterName: string) => {
    const actionParameters = _.clone(this.props.parameters);
    const newActionParameters = actionParameters.filter((p) => p.name !== parameterName);
    this.props.updateParams(newActionParameters);
  };

  changeActionParameter = (
    parameterName: string,
    symbol: ISymbol,
    typename: BackEndValueType,
    changeReason?: ESdChangeReason
  ) => {
    const actionParameters = this.props.parameters;
    const actionParameter = actionParameters.find((p) => p.name === parameterName);
    if (actionParameter) {
      actionParameter.symbol = symbol;
    } else {
      actionParameters.push({
        name: parameterName,
        symbol,
        cardinality: ECardinality.One,
        Typename: typename,
        isOptionalParameter: false
      });
    }
    const valuesToUpdate = {
      actionParameters
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const wFStartSymbolsGroup = withContext<IWFStartSymbolsGroupProps>(
  withSD<IWFStartSymbolsGroupProps>(WFStartSymbolsGroup)
);
export { wFStartSymbolsGroup as WFStartSymbolsGroup };
